/**
 * MODULE: AssistantPerplexityComposer.tsx
 * PURPOSE: Perplexity-style composer — rounded card, inline toolbar, voice input.
 * RECENT: 2026-06-18 [2.2.1450] — Shared by **`AIAssistantRightRail`**, full-page **`/langflow-assistant`**, and browser assistant panel.
 */

import {
  ChevronDownIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
  MicrophoneIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { AssistantConnectorsMenu } from './AssistantConnectorsMenu';
import type { AssistantConnectorItem } from './assistantConnectorTypes';
import { ASSISTANT_RAIL_COMPOSER_CLASS } from './assistantPageTheme';
import { AssistantVoiceWaveformButton } from './AssistantVoiceWaveformButton';
import { useAssistantVoiceInput } from './useAssistantVoiceInput';

export type AssistantPerplexityComposerProps = {
  input: string;
  onInputChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onSendText?: (text: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement>;
  placeholder: string;
  disabled: boolean;
  isLoading: boolean;
  onStop?: () => void;
  composerMaxHeightPx: number;
  browserAutomationEnabled?: boolean;
  onRunInBrowser?: () => void;
  topSlot?: ReactNode;
  modeLabel?: string;
  connectorCatalog?: AssistantConnectorItem[];
  connectorsLoading?: boolean;
  connectorsError?: string | null;
  connectorActiveCount?: number;
  isConnectorSelected?: (item: AssistantConnectorItem) => boolean;
  onToggleConnector?: (item: AssistantConnectorItem) => void;
  /** Dark Perplexity rail styling — minimal toolbar, bottom-anchored card. */
  immersive?: boolean;
};

export function AssistantPerplexityComposer({
  input,
  onInputChange,
  onKeyDown,
  onSend,
  onSendText,
  textareaRef,
  placeholder,
  disabled,
  isLoading,
  onStop,
  composerMaxHeightPx,
  browserAutomationEnabled = false,
  onRunInBrowser,
  topSlot,
  modeLabel = 'Assistant',
  connectorCatalog = [],
  connectorsLoading = false,
  connectorsError = null,
  connectorActiveCount = 0,
  isConnectorSelected = () => false,
  onToggleConnector = () => {},
  immersive = false,
}: AssistantPerplexityComposerProps) {
  const voiceDisabled = disabled || isLoading;
  const inputSnapshotRef = useRef(input);
  inputSnapshotRef.current = input;
  const [connectorsOpen, setConnectorsOpen] = useState(false);
  const [connectorsPanel, setConnectorsPanel] = useState<'root' | 'connectors'>('root');

  const {
    isSupported: isVoiceSupported,
    isDictating,
    isVoiceMode,
    toggleDictation,
    toggleVoiceMode,
  } = useAssistantVoiceInput({
    disabled: voiceDisabled,
    onAppendTranscript: (text) => {
      const prev = inputSnapshotRef.current;
      onInputChange(prev.trim() ? `${prev.trim()} ${text}` : text);
      requestAnimationFrame(() => textareaRef.current?.focus());
    },
    onVoiceModeSend: (text) => {
      if (onSendText) {
        onSendText(text);
      } else {
        onInputChange(text);
        onSend();
      }
    },
  });

  const syncHeight = (ta: HTMLTextAreaElement) => {
    ta.style.height = '0px';
    const next = Math.min(ta.scrollHeight, composerMaxHeightPx);
    ta.style.height = `${next}px`;
    ta.style.overflowY = ta.scrollHeight > composerMaxHeightPx ? 'auto' : 'hidden';
  };

  const showSendButton = !isVoiceMode && !(isLoading && onStop);
  const effectivePlaceholder = immersive
    ? 'Type / for search modes'
    : isVoiceMode
      ? 'Listening… speak your question'
      : isDictating
        ? 'Listening…'
        : placeholder;

  const shellClass = immersive ? ASSISTANT_RAIL_COMPOSER_CLASS : 'overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800';

  const textareaClass =
    'min-h-[2.5rem] w-full resize-none border-0 bg-transparent text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-gray-500';

  const iconBtnClass =
    'rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700';

  return (
    <div className={shellClass} data-testid="assistant-perplexity-composer">
      {topSlot && !immersive ? (
        <div className="border-b border-gray-100 px-3 pt-2 dark:border-gray-700">{topSlot}</div>
      ) : null}
      <div className={`px-4 ${immersive ? 'pt-3.5 pb-1' : 'pt-4 pb-2'}`}>
        <textarea
          ref={textareaRef}
          data-testid="assistant-composer-input"
          value={input}
          onChange={(e) => {
            onInputChange(e.target.value);
            syncHeight(e.target);
          }}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={effectivePlaceholder}
          disabled={disabled}
          className={textareaClass}
          style={{ maxHeight: composerMaxHeightPx }}
        />
      </div>
      <div className={`flex items-center justify-between gap-2 ${immersive ? 'px-3 pb-3' : 'px-3 pb-3'}`}>
        <div className="flex min-w-0 items-center gap-1.5">
          <AssistantConnectorsMenu
            catalog={connectorCatalog}
            loading={connectorsLoading}
            error={connectorsError}
            isSelected={isConnectorSelected}
            onToggle={onToggleConnector}
            activeCount={connectorActiveCount}
            disabled={disabled}
            tone={immersive ? 'immersive' : 'default'}
            open={connectorsOpen}
            onOpenChange={setConnectorsOpen}
            panel={connectorsPanel}
            onPanelChange={setConnectorsPanel}
          />
          {!immersive ? (
            <>
              <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-900/50 dark:text-gray-200">
                <MagnifyingGlassIcon className="h-3.5 w-3.5" aria-hidden />
                {connectorActiveCount > 1 ? `${connectorActiveCount} sources` : 'Search'}
                <ChevronDownIcon className="h-3 w-3 text-gray-400" aria-hidden />
              </span>
              {browserAutomationEnabled && onRunInBrowser ? (
                <button
                  type="button"
                  data-testid="assistant-run-in-browser"
                  onClick={onRunInBrowser}
                  disabled={disabled}
                  className="rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-900 transition hover:bg-sky-100 disabled:opacity-50 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-100"
                  title="Run in AI Browser"
                >
                  Browser
                </button>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!immersive ? (
            <span className="hidden items-center gap-0.5 text-xs text-gray-500 sm:inline-flex dark:text-gray-400">
              {modeLabel}
              <ChevronDownIcon className="h-3 w-3" aria-hidden />
            </span>
          ) : null}
          {immersive ? (
            <button
              type="button"
              data-testid="assistant-sources-chip-button"
              onClick={() => {
                setConnectorsPanel('connectors');
                setConnectorsOpen(true);
              }}
              disabled={disabled}
              className={[
                iconBtnClass,
                connectorActiveCount > 0 ? 'text-gray-200' : '',
              ].join(' ')}
              aria-label="Connectors and sources"
              title="Connectors and sources"
            >
              <CpuChipIcon className="h-5 w-5" />
            </button>
          ) : null}
          <button
            type="button"
            data-testid="assistant-voice-dictation-button"
            onClick={toggleDictation}
            disabled={voiceDisabled || !isVoiceSupported}
            className={[
              iconBtnClass,
              isDictating ? 'bg-red-500/20 text-red-400' : '',
              !isVoiceSupported ? 'cursor-not-allowed opacity-40' : '',
            ].join(' ')}
            aria-label={isDictating ? 'Stop dictation' : 'Dictate into message'}
            title={
              !isVoiceSupported
                ? 'Voice input is not supported in this browser'
                : isDictating
                  ? 'Stop dictation'
                  : 'Dictate into message'
            }
          >
            <MicrophoneIcon className="h-5 w-5" />
          </button>
          {isVoiceSupported ? (
            <AssistantVoiceWaveformButton
              active={isVoiceMode}
              disabled={voiceDisabled}
              onClick={toggleVoiceMode}
              immersive={immersive}
            />
          ) : null}
          {isLoading && onStop ? (
            <button
              type="button"
              onClick={onStop}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-600 text-white transition hover:bg-amber-700"
              aria-label="Stop generation"
            >
              <StopCircleIcon className="h-5 w-5" />
            </button>
          ) : showSendButton && input.trim() ? (
            <button
              type="button"
              data-testid="assistant-send-button"
              onClick={onSend}
              disabled={disabled || !input.trim()}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-900 text-white transition hover:bg-gray-800 disabled:opacity-40 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              aria-label="Send"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
